import classNames from 'classnames';
import React from 'react';

import {FunnelLogContextProvider} from '@udemy/funnel-tracking';
import {FeatureVariantAssignment} from '@udemy/graphql';
import {
    CertificationBundleUnit,
    CertificationExamVoucherBannerWrapper,
    VoucherUIRegion,
    CertificationVoucherBundleUnit,
} from '@udemy/react-certification-preparation';
import {HorizontalBundleUnit} from '@udemy/react-discovery-units';

import {BUNDLE_DISCOUNT_CODE} from '../constants';
import styles from '../topic.module.less';
import {type BundleVariant, type Topic, BUNDLE_VARIANT} from '../types/topic';

interface TopicBundleUnitProps {
    variant: BundleVariant;
    topicId: Topic['id'];
    isTopicVoucherModuleAboveBundle?: FeatureVariantAssignment;
    certVouchersShowAddToCart?: boolean;
}

export const TopicBundleUnit: React.FC<TopicBundleUnitProps> = ({
    variant,
    topicId,
    isTopicVoucherModuleAboveBundle,
    certVouchersShowAddToCart,
}) => {
    const voucherAbove =
        isTopicVoucherModuleAboveBundle?.configuration?.isVoucherModuleAbovebundle ?? false;

    const voucherUiRegion = (
        isTopicVoucherModuleAboveBundle?.configuration?.isTopicVoucherModuleAbovebundle
            ? 'above_course_bundle'
            : 'below_course_bundle'
    ) as VoucherUIRegion;

    const voucherBanner = (
        <CertificationExamVoucherBannerWrapper
            topicId={topicId}
            theme="dark"
            layout="horizontal"
            isTopicBanner
            experimentData={isTopicVoucherModuleAboveBundle}
            uiRegion={voucherUiRegion}
            showAddToCart={certVouchersShowAddToCart}
        />
    );

    const voucherContainerClass = classNames(styles['voucher-banner-bundle-unit-container'], {
        [styles['voucher-above']]: voucherAbove,
        [styles['voucher-below']]: !voucherAbove,
    });

    switch (variant.type) {
        case BUNDLE_VARIANT.CERTIFICATION:
            return (
                <div className={voucherContainerClass}>
                    {variant.showCertificationVoucherBundleUnit ? (
                        <FunnelLogContextProvider pageType="topic">
                            <CertificationVoucherBundleUnit bundle={variant.certificationBundle} />
                        </FunnelLogContextProvider>
                    ) : (
                        <div
                            className={classNames(
                                styles['certification-bundle-unit-wrapper'],
                                'component-margin',
                            )}
                            data-item-index={0}
                        >
                            <CertificationBundleUnit
                                certificationBundle={variant.certificationBundle}
                                isBundleDiscountEnabled
                                bundleDiscountCode={BUNDLE_DISCOUNT_CODE}
                            />
                        </div>
                    )}
                    {voucherBanner}
                </div>
            );
        case BUNDLE_VARIANT.GENERIC:
            return (
                <>
                    <div
                        className={classNames(
                            styles['certification-bundle-unit-wrapper'],
                            'component-margin',
                        )}
                        data-item-index={0}
                    >
                        <HorizontalBundleUnit
                            contents={variant.courses}
                            title={interpolate(
                                gettext(
                                    "Looking to advance your skills in %(topicName)s? We've got you.",
                                ),
                                {topicName: variant.topicName},
                                true,
                            )}
                            subtitle={gettext(
                                'Get everything you need to reach your goals in one convenient bundle.',
                            )}
                            valueProps={[
                                gettext('Top-rated courses'),
                                gettext('Popular with learners just like you'),
                                gettext('Guidance from real-world experts'),
                            ]}
                            {...(variant.enableSpecialBundlePricing
                                ? {
                                      isBundleDiscountEnabled: true,
                                      bundleDiscountCode: BUNDLE_DISCOUNT_CODE,
                                  }
                                : {})}
                        />
                    </div>
                    <div className={styles['voucher-banner-generic-bundle-unit']}>
                        {voucherBanner}
                    </div>
                </>
            );
        case BUNDLE_VARIANT.VOUCHER_ONLY:
            return <div className={voucherContainerClass}>{voucherBanner}</div>;
    }
};
